import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { adminService } from '../../services/api';

const Inventory = () => {
  const [inventory, setInventory] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    quantity: '',
    unit: '',
    price: '',
    categoryId: '',
    supplier: '',
    minimumStock: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const response = await adminService.getInventory();
      if (response.data.success) {
        setInventory(response.data.data.inventory);
        setError(null);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError('Envanter yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await adminService.getCategories();
      setCategories(response.data.filter(cat => cat.type === 'inventory'));
    } catch (err) {
      console.error('Kategoriler yüklenirken hata:', err);
    }
  };

  useEffect(() => {
    fetchInventory();
    fetchCategories();
  }, []);

  const handleOpenDialog = (item = null) => {
    if (item) {
      setSelectedItem(item);
      setFormData({
        name: item.name,
        description: item.description,
        quantity: item.quantity,
        unit: item.unit,
        price: item.price,
        categoryId: item.categoryId,
        supplier: item.supplier,
        minimumStock: item.minimumStock
      });
    } else {
      setSelectedItem(null);
      setFormData({
        name: '',
        description: '',
        quantity: '',
        unit: '',
        price: '',
        categoryId: '',
        supplier: '',
        minimumStock: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedItem(null);
    setFormData({
      name: '',
      description: '',
      quantity: '',
      unit: '',
      price: '',
      categoryId: '',
      supplier: '',
      minimumStock: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedItem) {
        await adminService.updateInventoryItem(selectedItem.id, formData);
      } else {
        await adminService.createInventoryItem(formData);
      }
      handleCloseDialog();
      fetchInventory();
    } catch (err) {
      setError('İşlem sırasında bir hata oluştu.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu ürünü silmek istediğinizden emin misiniz?')) {
      try {
        await adminService.deleteInventoryItem(id);
        fetchInventory();
      } catch (err) {
        setError('Ürün silinirken bir hata oluştu.');
      }
    }
  };

  // Filtrelenmiş ve aranmış envanter
  const filteredInventory = useMemo(() => {
    return inventory.filter(item => {
      // Arama
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        item.name.toLowerCase().includes(search) ||
        (item.description && item.description.toLowerCase().includes(search)) ||
        (item.supplier && item.supplier.toLowerCase().includes(search));
      // Kategori
      const matchesCategory = categoryFilter === 'all' || String(item.categoryId) === String(categoryFilter);
      // Stok durumu
      let matchesStock = true;
      if (stockFilter === 'low') {
        matchesStock = item.quantity <= item.minimumStock;
      } else if (stockFilter === 'active') {
        matchesStock = item.isActive !== false;
      } else if (stockFilter === 'inactive') {
        matchesStock = item.isActive === false;
      }
      return matchesSearch && matchesCategory && matchesStock;
    });
  }, [inventory, searchTerm, categoryFilter, stockFilter]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Filtre ve Arama Alanları */}
      <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={2} mb={3} alignItems={isMobile ? 'stretch' : 'center'}>
        <TextField
          placeholder="Ürün adı, açıklama veya tedarikçi ara..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          size={isMobile ? 'large' : 'small'}
          fullWidth={isMobile}
        />
        <FormControl fullWidth={isMobile} size={isMobile ? 'large' : 'small'}>
          <InputLabel>Kategori</InputLabel>
          <Select
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
            label="Kategori"
          >
            <MenuItem value="all">Tümü</MenuItem>
            {categories.map(cat => (
              <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth={isMobile} size={isMobile ? 'large' : 'small'}>
          <InputLabel>Stok Durumu</InputLabel>
          <Select
            value={stockFilter}
            onChange={e => setStockFilter(e.target.value)}
            label="Stok Durumu"
          >
            <MenuItem value="all">Tümü</MenuItem>
            <MenuItem value="low">Düşük Stok</MenuItem>
            <MenuItem value="active">Aktif</MenuItem>
            <MenuItem value="inactive">Pasif</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} justifyContent="space-between" alignItems={isMobile ? 'stretch' : 'center'} mb={3} gap={isMobile ? 2 : 0}>
        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1">
          Envanter Yönetimi
        </Typography>
        <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={isMobile ? 1 : 0}>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={fetchInventory}
            sx={{ mr: isMobile ? 0 : 1, mb: isMobile ? 1 : 0, width: isMobile ? '100%' : 'auto' }}
            size={isMobile ? 'large' : 'medium'}
            fullWidth={isMobile}
          >
            Yenile
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            size={isMobile ? 'large' : 'medium'}
            fullWidth={isMobile}
          >
            Yeni Ürün
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Masaüstü Tablo */}
      {!isMobile && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Ürün Adı</TableCell>
                <TableCell>Kategori</TableCell>
                <TableCell>Miktar</TableCell>
                <TableCell>Fiyat</TableCell>
                <TableCell>Tedarikçi</TableCell>
                <TableCell>Min. Stok</TableCell>
                <TableCell>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredInventory.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2">{item.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.description}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={categories.find(cat => cat.id === item.categoryId)?.name || item.category}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      color={item.quantity <= item.minimumStock ? 'error' : 'inherit'}
                    >
                      {item.quantity} {item.unit}
                    </Typography>
                  </TableCell>
                  <TableCell>{item.price} TL</TableCell>
                  <TableCell>{item.supplier}</TableCell>
                  <TableCell>{item.minimumStock}</TableCell>
                  <TableCell>
                    <Tooltip title="Düzenle">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(item)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Sil">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(item.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Mobil Kart Görünümü */}
      {isMobile && (
        <Box>
          {filteredInventory.map((item) => (
            <Card key={item.id} sx={{ mb: 2, p: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1" fontWeight={600}>{item.name}</Typography>
                <Chip
                  label={categories.find(cat => cat.id === item.categoryId)?.name || item.category}
                  size="small"
                  sx={{ ml: 1 }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {item.description}
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} mt={2}>
                <Chip label={`Miktar: ${item.quantity} ${item.unit}`} color={item.quantity <= item.minimumStock ? 'error' : 'default'} size="small" />
                <Chip label={`Fiyat: ${item.price} TL`} size="small" />
                <Chip label={`Min. Stok: ${item.minimumStock}`} size="small" />
                <Chip label={`Tedarikçi: ${item.supplier}`} size="small" />
              </Box>
              <Box display="flex" gap={1} mt={2}>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => handleOpenDialog(item)}
                  size="small"
                  fullWidth
                >
                  Düzenle
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleDelete(item.id)}
                  size="small"
                  fullWidth
                >
                  Sil
                </Button>
              </Box>
            </Card>
          ))}
        </Box>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedItem ? 'Ürünü Düzenle' : 'Yeni Ürün Ekle'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Ürün Adı"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  size={isMobile ? 'large' : 'medium'}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Açıklama"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={2}
                  size={isMobile ? 'large' : 'medium'}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Miktar"
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  required
                  size={isMobile ? 'large' : 'medium'}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Birim"
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  required
                  size={isMobile ? 'large' : 'medium'}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Fiyat (TL)"
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                  size={isMobile ? 'large' : 'medium'}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth required size={isMobile ? 'large' : 'medium'}>
                  <InputLabel>Kategori</InputLabel>
                  <Select
                    value={formData.categoryId}
                    onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                    label="Kategori"
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Tedarikçi"
                  value={formData.supplier}
                  onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                  required
                  size={isMobile ? 'large' : 'medium'}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Minimum Stok"
                  type="number"
                  value={formData.minimumStock}
                  onChange={(e) => setFormData({ ...formData, minimumStock: e.target.value })}
                  required
                  size={isMobile ? 'large' : 'medium'}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} size={isMobile ? 'large' : 'medium'}>İptal</Button>
            <Button type="submit" variant="contained" color="primary" size={isMobile ? 'large' : 'medium'}>
              {selectedItem ? 'Güncelle' : 'Ekle'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Inventory; 